import React, { useEffect, useState } from "react";
//import "./orderdetails.css";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_URL } from "../../../services/apiConfig";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axiosInstance from "../../../services/axios.instance";
import AFLSuccessModal from "../../../components/AFL/AFLSuccessModal";
import AFLErrorModal from "../../../components/AFL/AFLErrorModal";
import { ImagePopupModal } from "../../../components";
import { toast } from "react-toastify";

const EditServiceDetails = () => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const OrderId = new URLSearchParams(location.search).get("OrderId");
  //const { orderDetails } = location.state;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page") || "1";
  const size = searchParams.get("size") || "10";

  const handleBack = () => {
    navigate({
      pathname: "/dashboard/servicelist",
      search: `?page=${page}&size=${size}`,
    });
  };

  interface DeliveryPartnerLocation {
    longitude: any;
    latitude: any;
    location: string;
    order_status: string;
    delivery_partner_status: string;
    changed_at: string;
    distance_in_km: number;
  }
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  // State for location details
  const [pickupLocation, setPickupLocation] = useState({
    name: "",
    latitude: "",
    longitude: "",
  });

  const [isEditingPickup, setIsEditingPickup] = useState(false);
  const [editedPickup, setEditedPickup] = useState({
    name: "",
    latitude: "",
    longitude: "",
  });

  const [dropoffLocation, setDropoffLocation] = useState({
    name: "",
    latitude: "",
    longitude: "",
  });
  const [driverStartedDateTime, setDriverStartedDateTime] =
    useState<string>("");
  const [driverReachedDateTime, setDriverReachedDateTime] =
    useState<string>("");
  const [serviceStartedDateTime, setServiceStartedDateTime] =
    useState<string>("");
  const [serviceCompletedDateTime, setServiceCompletedDateTime] =
    useState<string>("");
  const [checkListImageUrls, setCheckListImageUrls] = useState<string[]>([]);
  const [deletedImageUrls, setDeletedImageUrls] = useState<string[]>([]);
  const [recollectedCheckListImageUrls, setRecollectedCheckListImageUrls] =
    useState<string[]>([]);
  const [deletedRecollectedImageUrls, setDeletedRecollectedImageUrls] =
    useState<string[]>([]);
  const [tripSheetUrl, setTripSheetUrl] = useState<string>("");
  const [comment, setComment] = useState<string>("");
  const { data: orderDetails } = useQuery({
    queryKey: ["orderDetails", OrderId],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `${API_URL.SERVICE_DETAILS}/${OrderId}`,
        {
          headers: {
            location: "0,0",
            language: "english",
          },
        }
      );
      return response.data;
    },
  });

  const formatDate = (isoDate: any) => {
    const date = new Date(isoDate);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  };
  //--------------------------------------------------------------------------
  const driverReachedTimeDifference = (createdAt: string) => {
    const createdDate = new Date(createdAt);
    const driverReachedTime =
      orderDetails?.order_status_history_responses?.find(
        (status: any) =>
          status.delivery_partner_status === "REACHED_CUSTOMER_LOCATION"
      )?.status_changed_at;

    if (!driverReachedTime) return { totalMinutes: 0, formatted: "N/A" };

    const driverReachedDate = new Date(driverReachedTime);
    const driverDiffInSeconds = Math.floor(
      (driverReachedDate.getTime() - createdDate.getTime()) / 1000
    );

    const hours = Math.floor(driverDiffInSeconds / 3600);
    const minutes = Math.floor((driverDiffInSeconds % 3600) / 60);
    const totalMinutes = hours * 60 + minutes;

    return { totalMinutes, formatted: formatTime(hours, minutes) };
  };

  const timeDifference = (createdAt: string) => {
    const now = new Date();
    const createdDate = new Date(createdAt);
    const diffInSeconds = Math.floor(
      (now.getTime() - createdDate.getTime()) / 1000
    );

    const hours = Math.floor(diffInSeconds / 3600);
    const minutes = Math.floor((diffInSeconds % 3600) / 60);
    const totalMinutes = hours * 60 + minutes;

    return { totalMinutes, formatted: formatTime(hours, minutes) };
  };

  const formatTime = (hours: number, minutes: number) => {
    if (hours > 0 && minutes > 0) {
      return `${hours} hour(s) ${minutes} minute(s) ago`;
    } else if (hours > 0) {
      return `${hours} hour(s) ago`;
    } else if (minutes > 0) {
      return `${minutes} minute(s) ago`;
    } else {
      return "Just now";
    }
  };
  // const { totalMinutes, formatted } = timeDifference(orderDetails?.createdAt);
  // const statusColor = totalMinutes < 100 ? "text-success" : "text-danger";
  const shouldUseTimeDifference =
    orderDetails?.aaa_service_request_model?.state === "initiate";
  const timeData = shouldUseTimeDifference
    ? timeDifference(orderDetails?.createdAt)
    : { totalMinutes: 0, formatted: "Just now" };

  // Determine status color based on conditions
  const statusColor = shouldUseTimeDifference
    ? timeData.totalMinutes < 10
      ? "text-success"
      : "text-danger"
    : orderDetails?.is_dispatched_within_time
    ? "text-success"
    : "text-danger";

  const { totalMinutes: totalMinutesDriverReached } =
    driverReachedTimeDifference(orderDetails?.createdAt);

  const driverReachedStatusColor =
    totalMinutesDriverReached < 60 ? "text-success" : "text-danger";

  const [showImagePopupModal, setShowImagePopupModal] = useState(false);
  const [imageSource, setImageSource] = useState("");
  const onClickVdrDocument = (imageUrl: string) => {
    setImageSource(imageUrl);
    setShowImagePopupModal(true);
  };

  // Update details mutation
  const { mutate: updateDetails, isPending: isUpdating } = useMutation({
    mutationFn: async (data: {}) => {
      const response = await axiosInstance.post(API_URL.SERVICE_DETAILS_EDIT, {
        order_number: orderDetails?.order_service_details?.orderNumber,
        ...data,
      });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ["orderDetails", OrderId] });
      setShowSuccessModal(true);
    },
    onError: () => {
      setShowErrorModal(true);
    },
  });

  // Fetch delivery partner location
  const { data: deliveryPartnerData = [] } = useQuery<
    DeliveryPartnerLocation[]
  >({
    queryKey: [
      "deliveryPartnerLocation",
      orderDetails?.deliveryPartnerId,
      orderDetails?.order_service_details?.orderNumber,
    ],
    queryFn: async () => {
      if (!orderDetails?.order_service_details?.orderNumber) {
        return [];
      }

      const payload = orderDetails?.deliveryPartnerId
        ? {
            delivery_partner_id: orderDetails.deliveryPartnerId,
            order_number: orderDetails.order_service_details.orderNumber,
          }
        : {
            service_from_app: "false",
            order_number: orderDetails.order_service_details.orderNumber,
          };

      const response = await axiosInstance.post(
        `${API_URL.DRIVER_LOCATION}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data || [];
    },
    enabled: !!orderDetails?.order_service_details?.orderNumber,
  });

  // Set initial values when orderDetails or deliveryPartnerData changes
  useEffect(() => {
    if (orderDetails) {
      // Set pickup location from delivery partner data
      const reachedLocation = deliveryPartnerData?.find(
        (status: any) =>
          status.delivery_partner_status === "REACHED_CUSTOMER_LOCATION"
      );

      if (reachedLocation) {
        setPickupLocation({
          name: reachedLocation.location || "",
          latitude: reachedLocation.latitude || "",
          longitude: reachedLocation.longitude || "",
        });
        setEditedPickup({
          name: reachedLocation.location || "",
          latitude: reachedLocation.latitude || "",
          longitude: reachedLocation.longitude || "",
        });
      }

      if (orderDetails?.order_service_details?.tripSheetUrl) {
        setTripSheetUrl(orderDetails.order_service_details.tripSheetUrl);
      }
      setCheckListImageUrls(
        orderDetails.products?.[0]?.checkListImageUrls || []
      );
      setRecollectedCheckListImageUrls(
        orderDetails.products?.[0]?.recollectedCheckListImageUrls || []
      );
      // Set initial driver started datetime if exists
      const acceptedStatus = orderDetails?.order_status_history_responses?.find(
        (status: any) => status.delivery_partner_status === "DELIVERY_ACCEPTED"
      );
      if (acceptedStatus?.status_changed_at) {
        // Use the exact datetime string from the server without timezone conversion
        const dateStr = acceptedStatus.status_changed_at;
        // Extract just the date and time parts (YYYY-MM-DDTHH:MM:SS)
        const dateTimeStr = dateStr.split(".")[0];
        // Truncate to just YYYY-MM-DDTHH:MM for the datetime-local input
        setDriverStartedDateTime(dateTimeStr.substring(0, 16));
      }

      // Set initial driver reached datetime if exists
      const reachedStatus = orderDetails?.order_status_history_responses?.find(
        (status: any) =>
          status.delivery_partner_status === "REACHED_CUSTOMER_LOCATION"
      );
      if (reachedStatus?.status_changed_at) {
        // Use the exact datetime string from the server without timezone conversion
        const dateStr = reachedStatus.status_changed_at;
        // Extract just the date and time parts (YYYY-MM-DDTHH:MM:SS)
        const dateTimeStr = dateStr.split(".")[0];
        // Truncate to just YYYY-MM-DDTHH:MM for the datetime-local input
        setDriverReachedDateTime(dateTimeStr.substring(0, 16));
      }

      // Set initial service started datetime if exists
      const serviceStartedStatus =
        orderDetails?.order_status_history_responses?.find(
          (status: any) =>
            status.delivery_partner_status ===
            (orderDetails?.order_service_details?.serviceType ===
            "DISTANCE_BASED"
              ? "VEHICLE_COLLECTED"
              : "SERVICE_STARTED")
        );
      if (serviceStartedStatus?.status_changed_at) {
        const dateStr = serviceStartedStatus.status_changed_at;
        const dateTimeStr = dateStr.split(".")[0];
        setServiceStartedDateTime(dateTimeStr.substring(0, 16));
      }

      // Set initial service completed datetime if exists
      const serviceCompletedStatus =
        orderDetails?.order_status_history_responses?.find(
          (status: any) => status.delivery_partner_status === "COMPLETED"
        );
      if (serviceCompletedStatus?.status_changed_at) {
        const dateStr = serviceCompletedStatus.status_changed_at;
        const dateTimeStr = dateStr.split(".")[0];
        setServiceCompletedDateTime(dateTimeStr.substring(0, 16));
      }
    }
  }, [orderDetails, deliveryPartnerData]);

  interface UpdateData {
    driver_started_date_time?: string;
    driver_reached_date_time?: string;
    service_started_date_time?: string;
    service_completed_date_time?: string;
    check_list_image_urls?: string[];
    recollected_check_list_image_urls?: string[];
    trip_sheet_url?: string;
    comment?: string;
    state?: string;
    delivery_partner_location?: Array<{
      latitude: string;
      longitude: string;
      delivery_partner_status: string;
      location: string;
    }>;
    dropoff_location?: {
      name: string;
      latitude: number;
      longitude: number;
    };
  }

  const handleUpdate = () => {
    // Validate pickup location fields
    if (
      !pickupLocation.name ||
      !pickupLocation.latitude ||
      !pickupLocation.longitude ||
      !dropoffLocation.name ||
      !dropoffLocation.latitude ||
      !dropoffLocation.longitude
    ) {
      toast.error(
        "Please fill in all Reached Pickup & DropOff location fields (Location Name, Latitude, and Longitude)"
      );
      return;
    }

    // Validate checklist images
    if (checkListImageUrls.length === 0) {
      toast.error("Please upload at least one checklist image");
      return;
    }

    if (recollectedCheckListImageUrls.length === 0) {
      toast.error("Please upload at least one recollected checklist image");
      return;
    }

    const updateData: UpdateData = {};
    const deliveryPartnerLocations = [];

    // Add pickup location
    deliveryPartnerLocations.push({
      latitude: pickupLocation.latitude,
      longitude: pickupLocation.longitude,
      delivery_partner_status: "REACHED_CUSTOMER_LOCATION",
      location: pickupLocation.name,
    });

    // Add dropoff location if any field is filled
    if (
      dropoffLocation.name ||
      dropoffLocation.latitude ||
      dropoffLocation.longitude
    ) {
      deliveryPartnerLocations.push({
        latitude: dropoffLocation.latitude,
        longitude: dropoffLocation.longitude,
        delivery_partner_status: "COMPLETED",
        location: dropoffLocation.name,
      });
    }

    // Add delivery partner locations to update data
    if (deliveryPartnerLocations.length > 0) {
      updateData.delivery_partner_location = deliveryPartnerLocations;
    }
    if (
      dropoffLocation.name &&
      dropoffLocation.latitude &&
      dropoffLocation.longitude
    ) {
      updateData.dropoff_location = {
        name: dropoffLocation.name,
        latitude: parseFloat(dropoffLocation.latitude),
        longitude: parseFloat(dropoffLocation.longitude),
      };
    }

    // Add datetime fields if they have values
    if (driverStartedDateTime) {
      updateData.driver_started_date_time = `${driverStartedDateTime}:00`;
    }
    if (driverReachedDateTime) {
      updateData.driver_reached_date_time = `${driverReachedDateTime}:00`;
    }
    if (serviceStartedDateTime) {
      updateData.service_started_date_time = `${serviceStartedDateTime}:00`;
    }
    if (serviceCompletedDateTime) {
      updateData.service_completed_date_time = `${serviceCompletedDateTime}:00`;
    }

    // Add image URLs if they exist
    if (checkListImageUrls.length > 0) {
      updateData.check_list_image_urls = checkListImageUrls;
    }
    if (recollectedCheckListImageUrls.length > 0) {
      updateData.recollected_check_list_image_urls =
        recollectedCheckListImageUrls;
    }

    // Add trip sheet URL if it exists
    if (tripSheetUrl) {
      updateData.trip_sheet_url = tripSheetUrl;
    }

    // Add comment and state to the update data
    if (comment) {
      updateData.comment = comment;
    }
    updateData.state = "COMPLETED";

    // Make the API call with all updates
    if (Object.keys(updateData).length > 0) {
      updateDetails(updateData);
    }
  };

  // Handle update location
  const handleUpdatePickupLocation = () => {
    // Create the update payload with the current state values
    const updatePayload = {
      delivery_partner_location: [
        {
          latitude: pickupLocation.latitude,
          longitude: pickupLocation.longitude,
          delivery_partner_status: "REACHED_CUSTOMER_LOCATION",
          location: pickupLocation.name,
        },
      ],
    };

    // Call the update function with the latest values
    updateDetails(updatePayload);
  };

  // Handle input change for pickup location
  const handlePickupLocationChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setPickupLocation((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleTripSheetUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset the input value to allow re-uploading the same file
    event.target.value = "";

    const formData = new FormData();
    formData.append("product_image", file);

    try {
      const response = await axiosInstance.post(
        API_URL.PRODUCT_IMAGE_UPLOAD,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.data) {
        setTripSheetUrl(response.data);
      }
    } catch (error) {
      console.error("Error uploading tripsheet:", error);
    }
  };

  const handleChecklistImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset the input value to allow re-uploading the same file
    event.target.value = "";

    const formData = new FormData();
    formData.append("product_image", file);

    try {
      const response = await axiosInstance.post(
        API_URL.PRODUCT_IMAGE_UPLOAD,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.data) {
        setCheckListImageUrls((prev) => [...prev, response.data]);
      }
    } catch (error) {
      console.error("Error uploading image:", error);
    }
  };

  const handleRecollectedChecklistImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset the input value to allow re-uploading the same file
    event.target.value = "";

    const formData = new FormData();
    formData.append("product_image", file);

    try {
      const response = await axiosInstance.post(
        API_URL.PRODUCT_IMAGE_UPLOAD,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.data) {
        setRecollectedCheckListImageUrls((prev) => [...prev, response.data]);
      }
    } catch (error) {
      console.error("Error uploading recollected image:", error);
    }
  };

  const handleDeleteImage = (urlToDelete: string) => {
    setCheckListImageUrls((prev) => prev.filter((url) => url !== urlToDelete));
    setDeletedImageUrls((prev) => [...prev, urlToDelete]);
  };

  const handleDeleteRecollectedImage = (urlToDelete: string) => {
    setRecollectedCheckListImageUrls((prev) =>
      prev.filter((url) => url !== urlToDelete)
    );
    setDeletedRecollectedImageUrls((prev) => [...prev, urlToDelete]);
  };

  return (
    <div>
      <div className="col-12">
        <div className="row">
          <div className="col-md-12 text-start div-bg-invoice-details pt-3">
            <div className="card card-bg-invoice-details">
              <div className="card-body px-5">
                <div>
                  <button onClick={handleBack} className="all_button_style">
                    Back
                  </button>
                </div>
                <div className="table-responsive pt-3">
                  <div className="mb-3 text-center invoice-text-yellow-afl">
                    <h4>
                      Job Reference No:{" "}
                      <span className="">
                        {orderDetails?.order_service_details?.referenceNumber}
                      </span>
                    </h4>
                  </div>
                  <div className="row pt-3">
                    {/* LEFT SIDE TABLE */}
                    <div className="col-md-6">
                      <div className="table-responsive">
                        <table className="table table-bordered">
                          <tbody>
                            <tr>
                              <th>Vehicle Make</th>
                              <td>
                                {
                                  orderDetails?.order_service_details
                                    ?.vehicleMake
                                }
                              </td>
                            </tr>
                            <tr>
                              <th>Vehicle Model</th>
                              <td>
                                {orderDetails?.order_service_details?.carModel}
                              </td>
                            </tr>
                            <tr>
                              <th>Mobile</th>
                              <td>
                                {
                                  orderDetails?.aaa_service_request_model
                                    ?.mobileNumber
                                }
                              </td>
                            </tr>
                            <tr>
                              <th>Plate Number</th>
                              <td>
                                {
                                  orderDetails?.aaa_service_request_model
                                    ?.plateNumber
                                }
                              </td>
                            </tr>
                            <tr>
                              <th>Chassis Number</th>
                              <td>{orderDetails?.chassis_number}</td>
                            </tr>
                            <tr>
                              <th>Service Date</th>
                              <td>{formatDate(orderDetails?.createdAt)}</td>
                            </tr>

                            {orderDetails?.recollection_location ? (
                              <>
                                <tr>
                                  <th>Location From</th>
                                  <td>
                                    {orderDetails?.delivery_location?.address}
                                  </td>
                                </tr>
                                <tr>
                                  <th>Location To</th>
                                  <td>
                                    {
                                      orderDetails?.recollection_location
                                        ?.address
                                    }
                                  </td>
                                </tr>
                              </>
                            ) : (
                              <tr>
                                <th>Location To</th>
                                <td>
                                  {orderDetails?.delivery_location?.address}
                                </td>
                              </tr>
                            )}

                            <tr>
                              <th>Comments AFL</th>
                              <td>
                                {
                                  orderDetails?.order_service_details
                                    ?.aflComments
                                }
                              </td>
                            </tr>

                            {orderDetails?.merchant_comment && (
                              <tr>
                                <th>Comments</th>
                                <td>{orderDetails?.merchant_comment}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* RIGHT SIDE TABLE */}
                    <div className="col-md-6">
                      <div className="table-responsive">
                        <table className="table table-bordered">
                          <tbody>
                            <tr>
                              <th>Customer Name</th>
                              <td>{orderDetails?.customer_name}</td>
                            </tr>
                            <tr>
                              <th>Service Type</th>
                              <td>
                                {
                                  orderDetails?.order_service_details
                                    ?.serviceName
                                }
                              </td>
                            </tr>
                            <tr>
                              <th>Service Status</th>
                              <td>
                                {orderDetails?.aaa_service_request_model?.state}
                              </td>
                            </tr>
                            <tr>
                              <th>Scheduled Date & Time (if any)</th>
                              <td>
                                {formatDate(
                                  orderDetails?.order_service_details
                                    ?.scheduledFor
                                )}
                              </td>
                            </tr>
                            <tr>
                              <th>Driver Name</th>
                              <td>
                                {orderDetails?.aaa_service_request_model
                                  ?.driverName ??
                                  orderDetails?.delivery_boy_details?.firstName}
                              </td>
                            </tr>
                            <tr>
                              <th>Driver Contact</th>
                              <td>
                                {orderDetails?.aaa_service_request_model
                                  ?.driverPhone ??
                                  orderDetails?.delivery_boy_details
                                    ?.phoneNumber}
                              </td>
                            </tr>
                            <tr>
                              <th>Comments from AAA agent</th>
                              <td>
                                {orderDetails?.aaa_service_request_model
                                  ?.comments?.length > 0 ? (
                                  orderDetails?.aaa_service_request_model?.comments.map(
                                    (comment: any, index: number) => (
                                      <div key={index}>
                                        {comment?.comment}
                                        <br />
                                      </div>
                                    )
                                  )
                                ) : (
                                  <span>No comments available</span>
                                )}
                              </td>
                            </tr>
                            <tr>
                              <th>Initiated DT</th>
                              <td>
                                {formatDate(orderDetails?.createdAt)}
                                <span className="text-muted">
                                  <span className={statusColor}> : STATUS</span>
                                </span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-12 pt-3">
                  <div className="invoice-div-head-afl">Comment</div>
                  <div className="invoice-div-data-afl">
                    <textarea
                      className="form-control"
                      rows={3}
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Enter your comment here..."
                    />
                  </div>
                </div>

                <div className="col-md-6 pt-3">
                  <div className="invoice-div-head-afl">Initiated DT</div>
                  <div className="invoice-div-data-afl">
                    {" "}
                    {formatDate(orderDetails?.createdAt)}
                    <span className="text-muted">
                      {/* {formatted} */}
                      <span className={statusColor}>: STATUS</span>
                    </span>
                  </div>
                </div>

                <div className="col-md-6 pt-3">
                  <div className="invoice-div-head-afl">
                    Driver Started DT<span className="merchantStar">*</span>
                  </div>
                  <div className="invoice-div-data-afl">
                    <input
                      type="datetime-local"
                      className="form-control form-control-sm"
                      value={driverStartedDateTime}
                      onChange={(e) => setDriverStartedDateTime(e.target.value)}
                      disabled={isUpdating}
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 pt-3">
                    <div className="invoice-div-head-afl">
                      Driver Reached DT<span className="merchantStar">*</span>
                    </div>
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1 me-2">
                        <input
                          type="datetime-local"
                          className="form-control form-control-sm"
                          value={driverReachedDateTime}
                          onChange={(e) =>
                            setDriverReachedDateTime(e.target.value)
                          }
                          disabled={isUpdating}
                        />
                      </div>
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() =>
                          updateDetails({
                            driver_reached_date_time: driverReachedDateTime,
                          })
                        }
                        disabled={!driverReachedDateTime || isUpdating}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                  <div className="col-md-6 pt-3">
                    <div className="invoice-div-head-afl">
                      Service Started DT <span className="merchantStar">*</span>
                    </div>
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1">
                        <input
                          type="datetime-local"
                          className="form-control form-control-sm"
                          value={serviceStartedDateTime}
                          onChange={(e) =>
                            setServiceStartedDateTime(e.target.value)
                          }
                          disabled={isUpdating}
                        />
                      </div>
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() =>
                          updateDetails({
                            service_started_date_time: serviceStartedDateTime,
                          })
                        }
                        disabled={!serviceStartedDateTime || isUpdating}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </div>

                <div className="col-md-6 pt-3">
                  <div className="invoice-div-head-afl">
                    Service Completed DT
                    <span className="merchantStar">*</span>
                  </div>
                  <div className="invoice-div-data-afl">
                    <input
                      type="datetime-local"
                      className="form-control form-control-sm"
                      value={serviceCompletedDateTime}
                      onChange={(e) =>
                        setServiceCompletedDateTime(e.target.value)
                      }
                      disabled={isUpdating}
                    />
                  </div>
                </div>

                <div className=" pt-3">
                  {/* Checklist Section */}
                  <div className="col-md-12 invoice-div-data-afl mb-2">
                    Checklist <span className="merchantStar">*</span>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="pt-3">
                        <input
                          type="file"
                          accept="image/*,application/pdf"
                          onChange={handleChecklistImageUpload}
                        />
                      </div>

                      {checkListImageUrls.map((url, index) => (
                        <div className="col-md-2 pt-3" key={index}>
                          {url?.endsWith(".pdf") ? (
                            <a
                              href={url}
                              download={`Checklist ${index + 1}.pdf`}
                            >
                              Download Document {index + 1}
                            </a>
                          ) : (
                            <div className="text-center border p-2 rounded">
                              <img
                                src={url}
                                onClick={() => onClickVdrDocument(url)}
                                alt={`Checklist ${index + 1}`}
                                style={{
                                  width: 75,
                                  height: 75,
                                  cursor: "pointer",
                                }}
                              />
                              <a
                                href={url}
                                download={`Checklist ${index + 1}.jpg`}
                                style={{
                                  display: "block",
                                  marginTop: "5px",
                                  fontSize: "14px",
                                }}
                              >
                                Download
                              </a>
                              <button
                                onClick={() => handleDeleteImage(url)}
                                className="btn btn-sm btn-danger mt-2"
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Recollected Checklist Section */}
                {orderDetails?.aaa_service_request_model?.state !== "start" && (
                  <div className="pt-3">
                    <div className="col-md-12 invoice-div-data-afl mb-2">
                      Recollected Checklist{" "}
                      <span className="merchantStar">*</span>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="pt-3">
                          <input
                            type="file"
                            accept="image/*,application/pdf"
                            onChange={handleRecollectedChecklistImageUpload}
                          />
                        </div>
                      </div>
                      <div className="row">
                        {recollectedCheckListImageUrls.map((url, index) => (
                          <div
                            className="col-6 col-sm-4 col-md-2 pt-3"
                            key={index}
                          >
                            {url.endsWith(".pdf") ? (
                              <a
                                href={url}
                                download={`Recollected_Checklist_${
                                  index + 1
                                }.pdf`}
                                style={{
                                  display: "block",
                                  margin: "10px 0",
                                  fontSize: "12px",
                                }}
                              >
                                Download Document {index + 1}
                              </a>
                            ) : (
                              <div className="text-center border p-2 rounded">
                                <img
                                  src={url}
                                  onClick={() => onClickVdrDocument(url)}
                                  alt={`Recollected Checklist ${index + 1}`}
                                  style={{
                                    width: 60,
                                    height: 60,
                                    cursor: "pointer",
                                    objectFit: "cover",
                                  }}
                                />
                                <a
                                  href={url}
                                  download={`Recollected_Checklist_${
                                    index + 1
                                  }.jpg`}
                                  style={{
                                    display: "block",
                                    marginTop: "5px",
                                    fontSize: "12px",
                                  }}
                                >
                                  Download
                                </a>
                                <button
                                  onClick={() =>
                                    handleDeleteRecollectedImage(url)
                                  }
                                  className="btn btn-sm btn-danger mt-2"
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                <div className="col-md-6 pt-3">
                  <div>
                    <div className="col-md-12 invoice-div-data-afl pt-3">
                      Tripsheet<span className="merchantStar">*</span>
                    </div>
                    {tripSheetUrl ? (
                      <div>
                        <a
                          href={tripSheetUrl}
                          download="Tripsheet.pdf"
                          className="invoice-approve-btn"
                          style={{
                            display: "inline-block",
                            padding: "8px 16px",
                            marginRight: "10px",
                          }}
                        >
                          Download Tripsheet
                        </a>
                        <div className="pt-3">
                          <input
                            type="file"
                            accept="image/*,application/pdf"
                            onChange={handleTripSheetUpload}
                          />
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="pt-3">
                          <input
                            type="file"
                            accept="image/*,application/pdf"
                            onChange={handleTripSheetUpload}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/* Delivery Partner Status Table */}
                <div className="col-md-12 pt-4">
                  <div className="invoice-div-head-afl">
                    Delivery Partner Status History
                  </div>
                  <div className="table-responsive">
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          <th>Partner Status</th>
                          <th>Location</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            Reached PickUp Location
                            <span className="merchantStar">*</span>
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Location Name"
                                name="name"
                                value={pickupLocation.name}
                                onChange={handlePickupLocationChange}
                              />
                              <div className="d-flex gap-1">
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Latitude"
                                  name="latitude"
                                  value={pickupLocation.latitude}
                                  onChange={handlePickupLocationChange}
                                />
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Longitude"
                                  name="longitude"
                                  value={pickupLocation.longitude}
                                  onChange={handlePickupLocationChange}
                                />
                              </div>
                              <button
                                className="btn btn-sm btn-success"
                                onClick={handleUpdatePickupLocation}
                                disabled={!pickupLocation.name}
                              >
                                Save
                              </button>
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            {orderDetails?.order_service_details
                              ?.serviceType === "DISTANCE_BASED"
                              ? "Reached DropOff Location"
                              : "Completed"}
                            <span className="merchantStar">*</span>
                          </td>
                          <td>
                            <div className="mb-2">
                              <input
                                type="text"
                                className="form-control form-control-sm mb-1"
                                placeholder="Location Name"
                                value={dropoffLocation.name}
                                onChange={(e) =>
                                  setDropoffLocation({
                                    ...dropoffLocation,
                                    name: e.target.value,
                                  })
                                }
                              />
                              <div className="d-flex gap-1">
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Latitude"
                                  value={dropoffLocation.latitude}
                                  onChange={(e) =>
                                    setDropoffLocation({
                                      ...dropoffLocation,
                                      latitude: e.target.value,
                                    })
                                  }
                                />
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Longitude"
                                  value={dropoffLocation.longitude}
                                  onChange={(e) =>
                                    setDropoffLocation({
                                      ...dropoffLocation,
                                      longitude: e.target.value,
                                    })
                                  }
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="row mt-4">
                  <div className="col-12 text-end">
                    <button
                      className="invoice-approve-btn px-4"
                      onClick={handleUpdate}
                      disabled={
                        isUpdating ||
                        !checkListImageUrls ||
                        !recollectedCheckListImageUrls ||
                        !driverStartedDateTime ||
                        !driverReachedDateTime ||
                        !serviceStartedDateTime ||
                        !serviceCompletedDateTime ||
                        !tripSheetUrl
                      }
                    >
                      {isUpdating ? "Updating..." : "COMPLETED"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ImagePopupModal
        isOpen={showImagePopupModal}
        //  onClose={onCloseImagePopupModal}
        src={imageSource}
        onClose={() => setShowImagePopupModal(false)}
        message={""}
      />
      {/* Success Modal */}
      <AFLSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        onNav={() => {
          navigate("/dashboard/servicelist");
          setShowSuccessModal(false);
        }}
        message="Details updated successfully"
      />
      {/* Error Modal */}
      <AFLErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message="Failed to update details. Please try again."
      />
    </div>
  );
};

export default EditServiceDetails;
